import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy, Output } from "@angular/core";
import { Events } from "ionic-angular";
import { FeatureToggleDataService } from "providers/features-service/feature-toggle.data.service";
import { BehaviorSubject, Subject } from "rxjs";
import { Observable } from "rxjs/Observable";
import { of } from "rxjs/observable/of";
import { catchError, map } from "rxjs/operators";
import SockJS from "sockjs-client";
import Stomp from "stompjs/lib/stomp.js";
import { Member } from "../../models/community-bereavement/member";
import { NavigationStatus } from "../../navigation/components/navigation-bar/nav-data";
import { ConfigurationService } from "../configuration/configuration.service";
import { EvaService } from "../eva/eva.service";

export interface FeatureToggle {
  name: string;
  active: boolean;
}

@Injectable()
export class StateManagementService implements OnDestroy {
  public logout$ = new Subject<void>();
  chartId: string = null;
  patientId: string = null; // patient.id
  selectedPatientData: any = null;
  patientCareType: String = "";

  patientChartStatus: String = "";
  navigationStatusData: any = null;
  navigationStatusNewData: NavigationStatus = null;
  assessmentId: string = null;
  assessmentUuid: string = null;
  userData: any = null;
  assessmentType: string = null;
  sectionId: string = null;
  idgId: string = null;
  pageId: string = null;
  previousPage: string = null;
  previousPageParams: any = null;
  currentPage: string = null;
  currentPageParams: any = null;
  assessmentUrl: any;
  idgDataUrl: any;
  private readonly isDev: boolean = false;

  //Community Bereavement
  selectedMemberData: Member = null;

  // For toggling the Order Management features
  @Output()
  showOrderManagement: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  @Output()
  isPatientChart: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  @Output()
  showCtiForm: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  @Output()
  showAdmitStandingOrders: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);
  @Output()
  hasPalliative: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  @Output()
  hasHospice: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  @Output()
  hasEvaluation: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  @Output()
  hadPalliative: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  @Output()
  hadHospice: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  @Output()
  hadEvaluation: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  @Output()
  showMAR: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  @Output()
  hasEligibility: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  HIS: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  // For toggling the new calendar features
  @Output()
  isNewCal: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  isPatientCal: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  // For toggling the new patient calendar from patient chart
  @Output()
  isPatientCalendarToggleOn: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  // For toggling the WestFax functionality
  @Output()
  isWestFaxToggleOn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  // For toggling the WorldView functionality
  @Output()
  isWorldViewFaxToggleOn: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  // For toggling Calendar Visit Start functionality
  @Output()
  isCalendarVisitStartOn: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  // For toggling the New Alerts
  @Output()
  showNewAlerts: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  // For toggling the new ePrescibe button
  @Output()
  isEPrescribe: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  PDB: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showVolunteer: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showVeteranManagement: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showProfessionalProviders: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showChangeOrganization: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showPRNVisit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showVoidVisit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showUpdateBilling: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showEfax: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showNonProviderClinicians: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showBackOffice: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showResourceManagement: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  showVolunteerResources: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  showCommunityBereavement: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  showPalliativeBilling: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  showSchedulerView: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showBillingReports: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showARDetailsDetails: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showARDetailsSummary: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showMonthlyRevenueSummary: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showPreBillingReports: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showLevelOfCareSummary: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showLevelOfCareDetails: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showUnrelatedDiagnosis: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showBillingRegisterReport: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  myData = new BehaviorSubject<any>(" ");

  showPatientBiometrics: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);
  showPocGoalsInterventions: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);
  combineHospicePalliative: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  showResourceReports: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  showNewCompany: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  showNewReport: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  showScheduledEligibility: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showAIVisitSummary: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );
  @Output()
  showAIReferralSummary: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  private stompClient;
  private stompClientComments;
  private stompClientActions;

  chartIdObservable: Observable<string>;
  chartIdObservers = [];

  patientIdObservable: Observable<string>;
  patientIdObservers = [];

  patientCareTypeObservable: Observable<String>; // possible values:  Hospice, Palliative, Evaluation, CBMember
  patientCareTypeObservers = [];

  patientChartStatusObservable: Observable<String>; // possible values:  Evaluation, Referral, On-service, Discharged
  patientChartStatusObservers = [];

  assessmentIdObservable: Observable<string>;
  assessmentIdObservers = [];

  assessmentUuidObservable: Observable<string>;
  assessmentUuidObservers = [];

  assessmentTypeObservable: Observable<string>;
  assessmentTypeObservers = [];

  cbMemberObservable: Observable<Member>;
  cbMemberObservers = [];

  sectionIdObservable: Observable<string>;
  sectionIdObservers = [];

  dataStateChangeObservable: Observable<string>;
  dataStateChangeObservers = [];

  idgIdObservable: Observable<string>;
  idgIdObservers = [];

  pageChangeObservable: Observable<string>;
  pageChangeObservers = [];

  pageIdObservable: Observable<string>;
  pageIdObservers = [];

  showPatientMedications: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);
  showPatientAllergies: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  featureServiceUrl: string;

  hisV2: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  ctiV2: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  diagnosisPlanCard: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  updateDiagnoses: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  monthEndClose: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  roomAndBoard: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  showPalliativeV1_5: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  patientBenefit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  addPocIssue: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showCancelVisit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showPalliativeCareTeam: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showOrderEntryOM: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showOEProviderPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showCAHPSRMS: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showCAHPSPinnacle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showCAHPSFazzi: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showCAHPSDEYTA: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showCAHPSSHP: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showCAHPSNexus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showSystemAdmin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showHis_v2EvalCarryoverTemp: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  show837Management: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showLegacyBilling: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showEPrescribe: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  locValidationReport: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  noeFilter: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  billingRoomNBoardToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  billingSequestrationToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  // CO-2885: Gates the MEC snapshot-availability resolver call + placeholder
  // rendering on month-end-close.html. Default-OFF; when the flag is absent
  // from the tenant's featureList, emitFeatureBuilder resolves to `false`
  // (see setupForFeatureList) which preserves the pre-CO-2885 render path
  // (raw QuickSight iframe for every month). Flip ON only after Sahil's
  // dataset SQL + the Redshift ingestion pipeline are live in the target
  // env, per TRD §10 rollout plan.
  @Output()
  mecSnapshotAvailabilityIndicatorToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  // CO-3334 (epic CO-1390): gates the Medicare hospice payer Requirements
  // editing + Rates → Hospice Visits tab. Default-OFF (dark release per
  // PRD §16); when absent from the tenant's featureList the payer screens
  // keep today's behavior (read-only Medicare Requirements per CO-3157,
  // no Hospice Visits tab). LD flag id `medicare_hospice_visit_billing`.
  @Output()
  medicareHospiceVisitBillingToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  // CO-3392 (epic CO-2315): gates the state-Medicaid payer classification
  // fields on the payer General tab (coverage type FFS/managed-care, state,
  // parent FFS payer, Medicare Advantage). Default-OFF (dark release); when
  // absent from the tenant's featureList the payer form is unchanged.
  // LD flag id `california_medi_cal_support`.
  @Output()
  californiaMediCalSupportToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  glExportToggle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  // CO-3309: gates the "Ask Eva" ellipsis-menu launcher button + the globally
  // mounted <eva-chat> component. Default-OFF (dark release); when absent
  // from the tenant's featureList neither surface renders. LD flag id
  // `ask_eva`.
  @Output()
  askEvaToggle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  // OPS-2858. Gates the Support Users tab. Matched on the RAW LaunchDarkly key -
  // see the emitFeatureBuilder entry below for why a display-name mapping would
  // silently never fire.
  supportUserManagementToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);
  // OPS-2873. Gates the Multi-Site Team tab in Company -> Work Force. Same RAW-key
  // convention as support_user_management above. Starts null, not false, so the tab
  // stays hidden until the toggle feed actually answers.
  multiSiteTeamToggle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  // CO-3441 (epic CO-3382): gates Concurrent Visits. Default-OFF; when absent
  // from the tenant's featureList emitFeatureBuilder resolves to `false`,
  // which preserves today's single-open-visit-per-patient behavior. LD flag
  // id `concurrent_visits`.
  @Output()
  concurrentVisitsToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  // CO-3840 (epic CO-3827): gates the Qapi+ entry in the Compliance report
  // tab. Default-OFF; when absent from the tenant's featureList
  // emitFeatureBuilder resolves to `false`, which keeps the entry out of
  // reports.ts's selectedTabs. Matched on the RAW LaunchDarkly key
  // `qapiplus_integration` (same convention as concurrent_visits/gl_export/
  // ask_eva - needs no flag_mappings.go registration).
  @Output()
  qapiPlusToggle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  // CO-2419: gates the two USER-FACING Q5001 surfaces — (1) "Q5001 -
  // Home/Residence" appearing in the Facility Type selectors, and (2) the
  // Care Location search being usable while Care Location Type is Home
  // (CO-3501). Default-OFF (dark release); when absent from the tenant's
  // featureList emitFeatureBuilder resolves to `false`, which keeps Q5001 out
  // of every dropdown and leaves Home greying out the search exactly as it
  // did pre-CO-2419.
  //
  // Deliberately does NOT gate the backend correctness changes in this epic —
  // the REQ-009 NPI union rule, the two REQ-010 R&B allow-lists, or the
  // REQ-031 837 facility loop. Those alter behavior for facility types other
  // than Q5001 (Q5003–Q5008, Q5010) and carry their own regression guards; a
  // Q5001 flag must not decide whether NPI validation is correct.
  //
  // LD flag id `q5001_home_residence`. See TRD-2026-022.
  @Output()
  q5001HomeResidenceToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showOverridesClaimErrors: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showHisExport: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showAnalytics: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  showIdgTempChanges: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  qaQueueToggle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  newBillingFacility: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  newBillingAllPayer: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showReferralAndIntake: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  legacyApplicationsIconToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showAiVoiceDictationTranscriptionComponents: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showAiIdgSummaryComponents: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showHOPEToggle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  @Output()
  showEligibilityToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showInstantEligibilityToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showChartMedsToggle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  @Output()
  showAssessmentUploadToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showHospiceAideUploadToggle: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  @Output()
  showDeathVisitToggle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  // For toggling the temp NCI functionality
  @Output()
  tempNciToggle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  // For toggling the LCD Compliance functionality
  @Output()
  lcdToggle: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  aiVoiceDictationTranscriptionComponents: Observable<boolean>;

  tokenRefreshedSubject = new BehaviorSubject<string | null>(null);
  isManualTokenRefreshInProgress = false;
  isRefreshingToken = false;

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private events: Events,
    private featureToggleDataService: FeatureToggleDataService,
    private evaService: EvaService
  ) {
    this.isDev = false;
    this.configurationService.configSubject$.subscribe((config: any) => {
      this.assessmentUrl = configurationService.getConfig().apiUrl;
      this.idgDataUrl = configurationService.resolveIdgUrl();
    });
  }

  //  ##############################
  //  Manage chart id
  //  ##############################

  public setupForFeatureList = () => {
    // This is used for toggling the Order Management features
    // Get default from the configuration item
    const errorCatcher = catchError((error) => {
      return of(false);
    });
    // Build a list of emitters
    // Friendly reminder: feature name/toggle must be in lowercase
    const featureEmitters = [
      this.emitFeatureBuilder("order management", "showOrderManagement"),
      this.emitFeatureBuilder("patient chart", "isPatientChart"),
      this.emitFeatureBuilder("cti", "showCtiForm"),
      this.emitFeatureBuilder("his", "HIS"),
      this.emitFeatureBuilder("palliative care", "hasPalliative"),
      this.emitFeatureBuilder("evaluation", "hasEvaluation"),
      this.emitFeatureBuilder(
        "admit standing orders",
        "showAdmitStandingOrders"
      ),
      this.emitFeatureBuilder("mar", "showMAR"),
      this.emitFeatureBuilder("newcal", "isNewCal"),
      this.emitFeatureBuilder("newPatientCal", "isPatientCal"),
      this.emitFeatureBuilder("patient calendar", "isPatientCalendarToggleOn"),
      this.emitFeatureBuilder("westfax", "isWestFaxToggleOn"),
      this.emitFeatureBuilder("worldviewfax", "isWorldViewFaxToggleOn"),
      this.emitFeatureBuilder("calendar visit start", "isCalendarVisitStartOn"),
      this.emitFeatureBuilder("newPatientEPrescribe", "isEPrescribe"),
      this.emitFeatureBuilder("pdb", "PDB"),
      this.emitFeatureBuilder("volunteer management", "showVolunteer"),
      this.emitFeatureBuilder("veteran management", "showVeteranManagement"),
      this.emitFeatureBuilder(
        "professional providers",
        "showProfessionalProviders"
      ),
      this.emitFeatureBuilder("change organization", "showChangeOrganization"),
      this.emitFeatureBuilder("prn visit", "showPRNVisit"),
      this.emitFeatureBuilder(
        "non provider clinicians",
        "showNonProviderClinicians"
      ),
      this.emitFeatureBuilder("back office", "showBackOffice"),
      this.emitFeatureBuilder("resource management", "showResourceManagement"),
      this.emitFeatureBuilder("volunteer resources", "showVolunteerResources"),
      this.emitFeatureBuilder(
        "community bereavement",
        "showCommunityBereavement"
      ),
      this.emitFeatureBuilder("palliative billing", "showPalliativeBilling"),
      this.emitFeatureBuilder("patient biometrics", "showPatientBiometrics"),
      this.emitFeatureBuilder("hospice care", "hasHospice"),
      this.emitFeatureBuilder("had hospice care", "hadHospice"),
      this.emitFeatureBuilder("had palliative care", "hadPalliative"),
      this.emitFeatureBuilder("had evaluation", "hadEvaluation"),
      this.emitFeatureBuilder("eligibility", "hasEligibility"),
      this.emitFeatureBuilder("scheduler view", "showSchedulerView"),
      this.emitFeatureBuilder("efax", "showEfax"),
      this.emitFeatureBuilder("unrelated diagnosis", "showUnrelatedDiagnosis"),
      this.emitFeatureBuilder("void visit", "showVoidVisit"),
      this.emitFeatureBuilder("update billing", "showUpdateBilling"),
      this.emitFeatureBuilder("new alerts", "showNewAlerts"),
      this.emitFeatureBuilder(
        "poc goals interventions",
        "showPocGoalsInterventions"
      ),
      this.emitFeatureBuilder(
        "combine hospice and palliative",
        "combineHospicePalliative"
      ),

      this.emitFeatureBuilder("billing reports", "showBillingReports"),
      this.emitFeatureBuilder("ar details details", "showARDetailsDetails"),
      this.emitFeatureBuilder("ar details summary", "showARDetailsSummary"),
      this.emitFeatureBuilder(
        "monthly revenue summary",
        "showMonthlyRevenueSummary"
      ),
      this.emitFeatureBuilder("pre-billing reports", "showPreBillingReports"),
      this.emitFeatureBuilder(
        "level of care summary",
        "showLevelOfCareSummary"
      ),
      this.emitFeatureBuilder(
        "level of care details",
        "showLevelOfCareDetails"
      ),
      this.emitFeatureBuilder("resource reports", "showResourceReports"),
      this.emitFeatureBuilder("new company", "showNewCompany"),
      this.emitFeatureBuilder("patient medications", "showPatientMedications"),
      this.emitFeatureBuilder("patient allergies", "showPatientAllergies"),
      this.emitFeatureBuilder("his_v2", "hisV2"),
      this.emitFeatureBuilder("cti_v2", "ctiV2"),
      this.emitFeatureBuilder("diagnosis plan card", "diagnosisPlanCard"),
      this.emitFeatureBuilder("update visit diagnoses", "updateDiagnoses"),
      this.emitFeatureBuilder("month end close", "monthEndClose"),
      this.emitFeatureBuilder("roomandboard", "roomAndBoard"),
      this.emitFeatureBuilder("palliative care v1.5", "showPalliativeV1_5"),
      this.emitFeatureBuilder("cancel visit", "showCancelVisit"),
      this.emitFeatureBuilder("palliative care team", "showPalliativeCareTeam"),
      this.emitFeatureBuilder("patient benefit", "patientBenefit"),
      this.emitFeatureBuilder("add poc issue", "addPocIssue"),
      this.emitFeatureBuilder("reporting ui 2.0", "showNewReport"),
      this.emitFeatureBuilder("order entry/om", "showOrderEntryOM"),
      this.emitFeatureBuilder("oe provider panel", "showOEProviderPanel"),
      this.emitFeatureBuilder("cahps_rms", "showCAHPSRMS"),
      this.emitFeatureBuilder("cahps_pinnacle", "showCAHPSPinnacle"),
      this.emitFeatureBuilder("cahps_fazzi", "showCAHPSFazzi"),
      this.emitFeatureBuilder("cahps_deyta", "showCAHPSDEYTA"),
      this.emitFeatureBuilder("cahps_shp", "showCAHPSSHP"),
      this.emitFeatureBuilder("cahps_nexus", "showCAHPSNexus"),
      this.emitFeatureBuilder("system admin", "showSystemAdmin"),
      this.emitFeatureBuilder(
        "his_v2 eval carryover temp",
        "showHis_v2EvalCarryoverTemp"
      ),
      this.emitFeatureBuilder("837 management", "show837Management"),
      this.emitFeatureBuilder(
        "overridesclaimerrors",
        "showOverridesClaimErrors"
      ),
      this.emitFeatureBuilder("loc validation report", "locValidationReport"),
      this.emitFeatureBuilder("noe filter", "noeFilter"),
      this.emitFeatureBuilder("legacy billing", "showLegacyBilling"),
      this.emitFeatureBuilder("idg temp", "showIdgTempChanges"),
      this.emitFeatureBuilder(
        "billing register report",
        "showBillingRegisterReport"
      ),
      this.emitFeatureBuilder("hisexportv1", "showHisExport"),
      this.emitFeatureBuilder(
        "billing room and board",
        "billingRoomNBoardToggle"
      ),
      this.emitFeatureBuilder(
        "billing_sequestration",
        "billingSequestrationToggle"
      ),
      // OPS-2880: the flag now exists, and the feed carries its RAW KEY.
      //
      // It was created in LaunchDarkly on 2026-09-02 as `mec_snapshot_availability_indicator`,
      // default OFF in all four environments. Read back from the live dev feed for tenant
      // 697190 the same day:
      //
      //     { id: "mec_snapshot_availability_indicator",
      //       name: "mec_snapshot_availability_indicator", active: false }
      //
      // `name` is the raw key because feature-toggle-functions maps a key to a display name
      // through flag_mappings.go and that service has no CI/CD -- its dev Lambda was last
      // deployed 2025-01-30 -- so a new mapping entry would never land. 44 of the feed's
      // entries are already in that state. Registering a display name would have produced a
      // flag that is ON, reports `active: true`, and never turns this toggle true.
      //
      // Turning it ON is a separate, deliberate step: it changes what every user sees on
      // every closed Month-End Close period, so Sri owns the QA pass and user comms first.
      this.emitFeatureBuilder(
        "mec_snapshot_availability_indicator",
        "mecSnapshotAvailabilityIndicatorToggle"
      ),
      this.emitFeatureBuilder(
        "medicare hospice visit billing",
        "medicareHospiceVisitBillingToggle"
      ),
      // CO-3392: Display Name "California Medi-Cal Support" lowercased —
      // must stay in step with MasterFeatureList.json + flag_mappings.go.
      this.emitFeatureBuilder(
        "california medi-cal support",
        "californiaMediCalSupportToggle"
      ),
      this.emitFeatureBuilder("gl_export", "glExportToggle"),
      this.emitFeatureBuilder("ask_eva", "askEvaToggle"),
      this.emitFeatureBuilder("qapiplus_integration", "qapiPlusToggle"),
      // OPS-2858. RAW LD key, deliberately NOT registered in flag_mappings.go or
      // MasterFeatureList.json: feature-toggle-functions has no CI/CD, so a
      // display-name mapping never deploys and the match silently never fires.
      // Serving true for org 697000 only.
      this.emitFeatureBuilder(
        "support_user_management",
        "supportUserManagementToggle"
      ),
      // OPS-2873. RAW LD key, for the same reason as support_user_management: do NOT add
      // it to flag_mappings.go or MasterFeatureList.json.
      this.emitFeatureBuilder("multi_site_team", "multiSiteTeamToggle"),
      this.emitFeatureBuilder("concurrent_visits", "concurrentVisitsToggle"),
      // CO-2419: matched on the RAW LaunchDarkly flag key `q5001_home_residence`,
      // not a display name — same pattern as ask_eva, billing_sequestration and
      // gl_export above (CO-3309).
      //
      // This originally matched the "Q5001 Home Residence" display name, which
      // only reaches the app if feature-toggle-functions deploys a
      // flag_mappings.go entry. That service has no CI/CD, so the mapping never
      // lands: its Go Lambdas in dev are from 2026-01-12, months behind main.
      // GetFeatureName then falls through to `return flagKey`, the feed carries
      // the raw key, and the display-name comparison never matched — Q5001 stayed
      // invisible with the flag switched ON and `active: true` in the response.
      //
      // Matching the raw key needs no service-side registration at all, which is
      // why the registry entries for this flag were removed rather than kept in
      // step: a flag_mappings entry would flip the feed back to the display name
      // and break this line the day that service ever does deploy.
      this.emitFeatureBuilder(
        "q5001_home_residence",
        "q5001HomeResidenceToggle"
      ),
      this.emitFeatureBuilder("qa queue", "qaQueueToggle"),
      this.emitFeatureBuilder("eprescribe", "showEPrescribe"),
      this.emitFeatureBuilder("new billing facility", "newBillingFacility"),
      this.emitFeatureBuilder("new billing all payer", "newBillingAllPayer"),
      this.emitFeatureBuilder("analytics", "showAnalytics"),
      this.emitFeatureBuilder("referral & intake", "showReferralAndIntake"),
      this.emitFeatureBuilder(
        "legacy applications icon",
        "legacyApplicationsIconToggle"
      ),
      this.emitFeatureBuilder(
        "ai voice dictation",
        "showAiVoiceDictationTranscriptionComponents"
      ),
      this.emitFeatureBuilder("ai-idg summary", "showAiIdgSummaryComponents"),
      this.emitFeatureBuilder("ai-visit summary", "showAIVisitSummary"),
      this.emitFeatureBuilder("ai-referral summary", "showAIReferralSummary"),
      this.emitFeatureBuilder("hope", "showHOPEToggle"),
      this.emitFeatureBuilder("benefits eligibility", "showEligibilityToggle"),
      this.emitFeatureBuilder(
        "instant-eligibility",
        "showInstantEligibilityToggle"
      ),
      this.emitFeatureBuilder("chartmeds", "showChartMedsToggle"),
      this.emitFeatureBuilder(
        "scheduled-eligibility",
        "showScheduledEligibility"
      ),
      this.emitFeatureBuilder(
        "convert document to visit",
        "showAssessmentUploadToggle"
      ),
      this.emitFeatureBuilder("death visit", "showDeathVisitToggle"),
      this.emitFeatureBuilder(
        "hospice aide upload",
        "showHospiceAideUploadToggle"
      ),
      this.emitFeatureBuilder("temp-nci", "tempNciToggle"),
      this.emitFeatureBuilder("lcd", "lcdToggle"),
    ];
    // Builds the callback for on success
    const onSuccessFeatureToggle = (emitterList: Function[]) => {
      return (featureList: FeatureToggle[]) => {
        if (
          featureList &&
          Array.isArray(featureList) &&
          featureList.length > 0
        ) {
          emitterList.forEach((item) => {
            item.call(this, featureList);
          });
        } else {
          featureEmitters.forEach((item) => {
            item.call(this, []);
          });
        }
      };
    };
    // Emits the value for any subscriber;
    of(this.featureToggleDataService.getAllStoredToggles())
      .pipe(errorCatcher)
      .subscribe(
        (storedToggles: FeatureToggle[]) => {
          // Handle success: storedToggles contains the toggles
          onSuccessFeatureToggle(featureEmitters).call(this, storedToggles);
        },
        (error) => {
          // Handle error: errorCatcher will catch and handle the error
          errorCatcher(error);
        }
      );
  };

  private emitFeatureBuilder(featureName: string, featureEmitterName: string) {
    const searchFeature = (item) => item.name.toLowerCase() === featureName;
    return (featureList: FeatureToggle[]) => {
      const feature = featureList.find(searchFeature);
      const newValue = feature ? feature.active : false;
      if (feature && feature.name.toLocaleLowerCase() === "patient chart") {
        if (this.isPatientChart.getValue() !== newValue) {
          this.isPatientChart.next(newValue);
        }
      }
      const subject = this[featureEmitterName];
      if (subject && subject.getValue() !== newValue) {
        subject.next(newValue);
      }
    };
  }

  getChartId() {
    return this.chartId;
  }

  setChartId(id: string) {
    if (this.chartId !== id) {
      this.chartId = id;
      this.notifyChartIdChange();
    }
  }

  getPatientId() {
    return this.patientId;
  }

  setPatientId(id: string) {
    if (this.patientId !== id) {
      this.patientId = id;
      this.notifyPatientIdChange();
    }
  }

  getChartIdObserver(): Observable<string> {
    if (!this.chartIdObservable) {
      this.chartIdObservable = new Observable<string>((observer) => {
        this.chartIdObservers = [];
        this.chartIdObservers.push(observer);
      });
    }
    return this.chartIdObservable;
  }

  private notifyChartIdChange(): void {
    for (let observer of this.chartIdObservers) {
      observer.next(this.chartId);
    }
  }

  getPatientIdObserver(): Observable<string> {
    if (!this.patientIdObservable) {
      this.patientIdObservable = new Observable<string>((observer) => {
        this.patientIdObservers = [];
        this.patientIdObservers.push(observer);
      });
    }
    return this.patientIdObservable;
  }

  private notifyPatientIdChange(): void {
    for (let observer of this.patientIdObservers) {
      observer.next(this.patientId);
    }
  }

  setPageId(id: string) {
    if (this.pageId !== id) {
      this.pageId = id;
      this.notifyPageIdChange();
    }
  }

  getPageId() {
    return this.pageId;
  }

  getPageIdObserver(): Observable<string> {
    if (!this.pageIdObservable) {
      this.pageIdObservable = new Observable<string>((observer) => {
        this.pageIdObservers.push(observer);
      });
    }
    return this.pageIdObservable;
  }

  private notifyPageIdChange(): void {
    for (let observer of this.pageIdObservers) {
      observer.next(this.pageId);
    }
  }

  //  ##############################
  //  Manage assessment id
  //  ##############################

  getAssessmentId() {
    return this.assessmentId;
  }

  setAssessmentId(id: string) {
    if (this.assessmentId !== id) {
      this.assessmentId = id;
      this.notifyAssessmentIdChange();
    }
  }

  getAssessmentIdObserver(): Observable<string> {
    if (!this.assessmentIdObservable) {
      this.assessmentIdObservable = new Observable<string>((observer) => {
        this.assessmentIdObservers.push(observer);
      });
    }
    return this.assessmentIdObservable;
  }

  private notifyAssessmentIdChange(): void {
    for (let observer of this.assessmentIdObservers) {
      observer.next(this.assessmentId);
    }
  }

  //  ##############################
  //  Manage assessment uuid
  //  ##############################

  getAssessmentUuid() {
    return this.assessmentUuid;
  }

  setAssessmentUuid(id: string) {
    if (this.assessmentUuid !== id) {
      this.assessmentUuid = id;
      this.notifyAssessmentUuidChange();
    }
  }

  getAssessmentUuidObserver(): Observable<string> {
    if (!this.assessmentUuidObservable) {
      this.assessmentUuidObservable = new Observable<string>((observer) => {
        this.assessmentUuidObservers.push(observer);
      });
    }
    return this.assessmentUuidObservable;
  }

  private notifyAssessmentUuidChange(): void {
    for (let observer of this.assessmentUuidObservers) {
      observer.next(this.assessmentUuid);
    }
  }

  //  ##############################
  //  Manage assessment type
  //  ##############################

  getAssessmentType() {
    return this.assessmentType;
  }

  setAssessmentType(id: string) {
    if (this.assessmentType !== id) {
      this.assessmentType = id;
      this.notifyAssessmentTypeChange();
    }
  }

  getAssessmentTypeObserver(): Observable<string> {
    if (!this.assessmentTypeObservable) {
      this.assessmentTypeObservable = new Observable<string>((observer) => {
        this.assessmentTypeObservers.push(observer);
      });
    }
    return this.assessmentTypeObservable;
  }

  private notifyAssessmentTypeChange(): void {
    for (let observer of this.assessmentTypeObservers) {
      observer.next(this.assessmentType);
    }
  }

  public navigationStatus() {
    return this.navigationStatusData;
  }

  public get hasNavigationStatus() {
    return (
      this.navigationStatusData &&
      this.navigationStatusData["asmtTypeLookupCode"]
    );
  }

  public setNavigationStatus(data) {
    this.navigationStatusData = data;
  }

  public newNavigationStatus() {
    return this.navigationStatusNewData;
  }

  public get hasNewNavigationStatus() {
    return (
      this.navigationStatusNewData &&
      this.navigationStatusNewData.asmtTypeLookupCode
    );
  }

  public setNewNavigationStatus(data) {
    this.events.publish("setNewNavigationStatus", data);
    this.navigationStatusNewData = data;
  }

  //  ##############################
  //  Manage section id
  //  ##############################

  public get hasPatientData() {
    return this.selectedPatientData && this.selectedPatientData.externalId;
  }

  public updateSelectedPatient(newPatient) {
    // this.selectedPatientData = JSON.parse(JSON.stringify(newPatient));
    this.selectedPatientData = newPatient;
  }

  checkUserDisciplines(): Observable<any> {
    return new Observable((observer) => {
      this.http.get(`${this.idgDataUrl}/company-resources/users/`).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  setSectionId(id: string) {
    if (this.sectionId !== id) {
      this.sectionId = id;
      this.notifySectionIdChange();
    }
  }

  getSectionId() {
    return this.sectionId;
  }

  getSectionIdObserver(): Observable<string> {
    if (!this.sectionIdObservable) {
      this.sectionIdObservable = new Observable<string>((observer) => {
        this.sectionIdObservers.push(observer);
      });
    }
    return this.sectionIdObservable;
  }

  private notifySectionIdChange(): void {
    for (let observer of this.sectionIdObservers) {
      observer.next(this.sectionId);
    }
  }

  //  ##############################
  //  Manage CB Member
  //  ##############################

  //@TODO: SMS - not sure if we need this for CB. Leaving this in for now
  public get hasCBMemberData() {
    return this.selectedMemberData && this.selectedMemberData.id;
  }

  public getCurrentCBMember(): Member {
    return this.selectedMemberData;
  }

  public updateSelectedCBMember(memberData: Member) {
    this.selectedMemberData = memberData;
    this.notifyCBMemberChange();
  }

  getCBMemberObserver(): Observable<Member> {
    if (!this.cbMemberObservable) {
      this.cbMemberObservable = new Observable<Member>((observer) => {
        this.cbMemberObservers.push(observer);
      });
    }
    return this.cbMemberObservable;
  }

  private notifyCBMemberChange(): void {
    for (let observer of this.cbMemberObservers) {
      observer.next(this.selectedMemberData);
    }
  }

  //  ##############################
  //  Manage data state changes
  //  ##############################

  getDataStateChangeObserver(): Observable<string> {
    if (!this.dataStateChangeObservable) {
      this.dataStateChangeObservable = new Observable<string>((observer) => {
        this.dataStateChangeObservers.push(observer);
      });
    }
    return this.dataStateChangeObservable;
  }

  notifyDataStateChange(): void {
    for (let observer of this.dataStateChangeObservers) {
      observer.next();
    }
  }

  getUserData() {
    return this.userData;
  }

  setUserData(data: any) {
    this.userData = data;
  }

  //  ##############################
  //  Manage Pages
  //  ##############################

  getPreviousPage() {
    return this.previousPage;
  }

  getPreviousPageParams() {
    return this.previousPageParams;
  }

  setPreviousPage(previousPage: string) {
    if (this.previousPage !== previousPage) {
      this.previousPage = previousPage;
    }
  }

  getCurrentPage() {
    return this.currentPage;
  }

  setCurrentPage(currentPage: string, navParams: any) {
    if (this.currentPage !== currentPage) {
      this.previousPage = this.currentPage;
      this.currentPageParams = this.currentPageParams
        ? this.currentPageParams
        : navParams;
      this.previousPageParams = this.currentPageParams;
      this.currentPage = currentPage;
      this.currentPageParams = navParams;
      this.notifyPageChange();
      this.savePrevPageToLocalStorage();
    }
  }

  savePrevPageToLocalStorage() {
    window.localStorage.setItem("prevPage", this.previousPage);
    window.localStorage.setItem(
      "prevPageParams",
      JSON.stringify(this.previousPageParams)
    );
  }

  getPageChangeObserver(): Observable<string> {
    if (!this.pageChangeObservable) {
      this.pageChangeObservable = new Observable<string>((observer) => {
        this.pageChangeObservers.push(observer);
      });
    }
    return this.pageChangeObservable;
  }

  notifyPageChange(): void {
    for (let observer of this.pageChangeObservers) {
      observer.next();
    }
  }

  //  ##############################
  //  Manage IDG ID
  //  ##############################

  getIdgUserId() {
    return this.idgId;
  }

  setIdgUserId(id: string) {
    if (this.idgId !== id) {
      this.idgId = id;
      this.notifyIdgIdChange();
    }
  }

  getIdgUserIdObserver(): Observable<string> {
    if (!this.idgIdObservable) {
      this.idgIdObservable = new Observable<string>((observer) => {
        this.idgIdObservers.push(observer);
      });
    }
    return this.idgIdObservable;
  }

  private notifyIdgIdChange(): void {
    for (let observer of this.idgIdObservers) {
      observer.next(this.idgId);
    }
  }

  //
  // TODO: Manage page change / section save ???
  //

  //  ##############################
  //  Manage websocket connection
  //  ##############################

  public initializeWebsocketConnectionForMeetingSignatures(
    dataUrl: string,
    listeningEndpoint: string,
    meetingId: number,
    onMessage: Function,
    onError: Function = (err) => {
      console.log("ERROR", err);
    }
  ) {
    let ws = new SockJS(dataUrl);
    this.stompClient = Stomp.over(ws);
    this.stompClient.reconnect_delay = 3000;
    this.stompClient.connect(
      {},
      (frame) => {
        this.stompClient.subscribe(listeningEndpoint, (message) => {
          onMessage(message);
        });
      },
      (err) => {
        onError(err);
      }
    );
    return this.stompClient;
  }

  public subscribeSignatures(listeningEndpoint: string, onMessage: Function) {
    return this.stompClient.subscribe(listeningEndpoint, (message) => {
      onMessage(message);
    });
  }

  public initializeWebsocketConnectionForMeetingComments(
    dataUrl: string,
    listeningEndpoint: string,
    meetingId: number,
    onMessage: Function,
    onError: Function = (err) => {
      console.log("ERROR", err);
    }
  ) {
    let ws = new SockJS(dataUrl);
    this.stompClientComments = Stomp.over(ws);
    this.stompClientComments.reconnect_delay = 3000;
    this.stompClientComments.connect(
      {},
      (frame) => {
        this.subscribeComments(listeningEndpoint, onMessage);
      },
      (err) => {
        onError(err);
      }
    );

    return this.stompClientComments;
  }

  public subscribeComments(listeningEndpoint: string, onMessage: Function) {
    return this.stompClientComments.subscribe(listeningEndpoint, (message) => {
      onMessage(message);
    });
  }

  public initializeWebsocketConnectionForMeetingActions(
    dataUrl: string,
    listeningEndpoint: string,
    meetingId: number,
    onMessage: Function,
    onError: Function = (err) => {
      console.error("ERROR", err);
    }
  ) {
    let ws = new SockJS(dataUrl);
    this.stompClientActions = Stomp.over(ws);
    this.stompClientActions.reconnect_delay = 3000;
    this.stompClientActions.connect(
      {},
      (frame) => {
        this.stompClientActions.subscribe(listeningEndpoint, (message) => {
          onMessage(message);
        });
      },
      (err) => {
        onError(err);
      }
    );
    return this.stompClientActions;
  }

  public getWebsocketConnection() {
    return this.stompClient;
  }

  public getWebsocketConnectionComment() {
    return this.stompClientComments;
  }

  public getWebsocketConnectionActions() {
    return this.stompClientActions;
  }

  public websocketIsInitialized(): boolean {
    return !!this.stompClient;
  }

  public websocketIsInitializedComments(): boolean {
    return !!this.stompClientComments;
  }

  public websocketIsInitializedActions(): boolean {
    return !!this.stompClientActions;
  }

  public disconnectWebsocketConnection() {
    this.stompClient.disconnect();
    this.stompClient = null;
  }

  public disconnectWebsocketConnectionComments() {
    this.stompClientComments.disconnect();
    this.stompClientComments = null;
  }

  public disconnectWebsocketConnectionActions() {
    this.stompClientActions.disconnect();
    this.stompClientActions = null;
  }

  get isDevEnv() {
    return this.isDev;
  }

  //  ##############################
  //  Reset state on logout
  //  ##############################

  resetState(): void {
    this.logout$.next();
    this.chartId = null;
    this.patientId = null;
    this.selectedPatientData = null;
    this.patientCareType = "";
    this.patientChartStatus = "";
    this.navigationStatusData = null;
    this.navigationStatusNewData = null;
    this.assessmentId = null;
    this.assessmentUuid = null;
    this.userData = null;
    this.assessmentType = null;
    this.sectionId = null;
    this.idgId = null;
    this.pageId = null;
    this.previousPage = null;
    this.previousPageParams = null;
    this.currentPage = null;
    this.currentPageParams = null;
    this.selectedMemberData = null;

    // Reset all BehaviorSubjects to their initial values
    this.showOrderManagement.next(false);
    this.isPatientChart.next(false);
    this.showCtiForm.next(null);
    this.showAdmitStandingOrders.next(null);
    this.hasPalliative.next(null);
    this.hasHospice.next(null);
    this.hasEvaluation.next(null);
    this.hadPalliative.next(null);
    this.hadHospice.next(null);
    this.hadEvaluation.next(null);
    this.showMAR.next(null);
    this.hasEligibility.next(null);
    this.HIS.next(null);
    this.isNewCal.next(null);
    this.isPatientCal.next(null);
    this.isPatientCalendarToggleOn.next(null);
    this.isWestFaxToggleOn.next(null);
    this.isWorldViewFaxToggleOn.next(null);
    this.isCalendarVisitStartOn.next(null);
    this.showNewAlerts.next(null);
    this.isEPrescribe.next(null);
    this.PDB.next(null);
    this.showVolunteer.next(null);
    this.showVeteranManagement.next(null);
    this.showProfessionalProviders.next(null);
    this.showChangeOrganization.next(null);
    this.showPRNVisit.next(null);
    this.showVoidVisit.next(null);
    this.showUpdateBilling.next(null);
    this.showEfax.next(null);
    this.showNonProviderClinicians.next(null);
    this.showBackOffice.next(null);
    this.showResourceManagement.next(null);
    this.showVolunteerResources.next(null);
    this.showCommunityBereavement.next(null);
    this.showPalliativeBilling.next(null);
    this.showSchedulerView.next(null);
    this.showBillingReports.next(null);
    this.showARDetailsDetails.next(null);
    this.showARDetailsSummary.next(null);
    this.showMonthlyRevenueSummary.next(null);
    this.showPreBillingReports.next(null);
    this.showLevelOfCareSummary.next(null);
    this.showLevelOfCareDetails.next(null);
    this.showUnrelatedDiagnosis.next(null);
    this.showBillingRegisterReport.next(null);
    this.showPatientBiometrics.next(null);
    this.showPocGoalsInterventions.next(null);
    this.combineHospicePalliative.next(null);
    this.showResourceReports.next(null);
    this.showNewCompany.next(null);
    this.showNewReport.next(null);
    this.showScheduledEligibility.next(null);
    this.showPatientMedications.next(null);
    this.showPatientAllergies.next(null);
    this.hisV2.next(null);
    this.ctiV2.next(null);
    this.diagnosisPlanCard.next(null);
    this.updateDiagnoses.next(null);
    this.monthEndClose.next(null);
    this.roomAndBoard.next(null);
    this.showPalliativeV1_5.next(null);
    this.patientBenefit.next(null);
    this.addPocIssue.next(null);
    this.showCancelVisit.next(null);
    this.showPalliativeCareTeam.next(null);
    this.showOrderEntryOM.next(null);
    this.showOEProviderPanel.next(null);
    this.showCAHPSRMS.next(null);
    this.showCAHPSPinnacle.next(null);
    this.showCAHPSFazzi.next(null);
    this.showCAHPSDEYTA.next(null);
    this.showCAHPSSHP.next(null);
    this.showCAHPSNexus.next(null);
    this.showSystemAdmin.next(null);
    this.showHis_v2EvalCarryoverTemp.next(null);
    this.show837Management.next(null);
    this.showLegacyBilling.next(null);
    this.showEPrescribe.next(null);
    this.locValidationReport.next(null);
    this.noeFilter.next(null);
    this.billingRoomNBoardToggle.next(null);
    this.billingSequestrationToggle.next(null);
    this.mecSnapshotAvailabilityIndicatorToggle.next(null);
    this.glExportToggle.next(null);
    this.askEvaToggle.next(null);
    this.supportUserManagementToggle.next(null);
    this.multiSiteTeamToggle.next(null);
    this.concurrentVisitsToggle.next(null);
    this.qapiPlusToggle.next(null);
    // The Ask Eva panel keeps its own session state, and it is an app-scoped
    // service that a sign-out cannot tear down, so it has to be told (CO-3559).
    this.evaService.reset();
    this.q5001HomeResidenceToggle.next(null);
    this.showOverridesClaimErrors.next(null);
    this.showHisExport.next(null);
    this.showAnalytics.next(null);
    this.showIdgTempChanges.next(null);
    this.qaQueueToggle.next(null);
    this.newBillingFacility.next(null);
    this.newBillingAllPayer.next(null);
    this.showReferralAndIntake.next(null);
    this.legacyApplicationsIconToggle.next(null);
    this.showAiVoiceDictationTranscriptionComponents.next(null);
    this.showAiIdgSummaryComponents.next(null);
    this.showAIVisitSummary.next(null);
    this.showAIReferralSummary.next(null);
    this.showHOPEToggle.next(null);
    this.showEligibilityToggle.next(null);
    this.showInstantEligibilityToggle.next(null);
    this.showChartMedsToggle.next(null);
    this.showAssessmentUploadToggle.next(null);
    this.showHospiceAideUploadToggle.next(null);
    this.showDeathVisitToggle.next(null);
    this.tempNciToggle.next(null);
    this.lcdToggle.next(null);
    this.myData.next(" ");
    this.tokenRefreshedSubject.next(null);
    this.isManualTokenRefreshInProgress = false;
    this.isRefreshingToken = false;
  }

  //  ##############################
  //  Maintenance methods
  //  ##############################

  ngOnDestroy() {
    for (let observer of this.chartIdObservers) {
      observer.complete();
      observer.unsubscribe();
    }
    for (let observer of this.patientIdObservers) {
      observer.complete();
      observer.unsubscribe();
    }
    for (let observer of this.assessmentIdObservers) {
      observer.complete();
      observer.unsubscribe();
    }
    for (let observer of this.assessmentUuidObservers) {
      observer.complete();
      observer.unsubscribe();
    }
    for (let observer of this.sectionIdObservers) {
      observer.complete();
      observer.unsubscribe();
    }
    for (let observer of this.pageIdObservers) {
      observer.complete();
      observer.unsubscribe();
    }
    for (let observer of this.dataStateChangeObservers) {
      observer.complete();
      observer.unsubscribe();
    }

    this.configurationService.configSubject$.unsubscribe();
  }

  CurrentData = this.myData.asObservable();
  setMyData(data: any) {
    this.myData.next(data);
  }

  getMyData() {
    return this.myData;
  }

  //  ##############################
  //  Publish Care Type changes (hospice vs palliative)
  //  ##############################

  public get hasPatientCareType() {
    return this.patientCareType && this.patientCareType !== "";
  }

  public getPatientCareType(): String {
    return this.patientCareType;
  }

  public updatePatientCareType(careType: String) {
    this.patientCareType = careType;
    this.notifyPatientCareTypeChange();
  }

  getPatientCareTypeObserver(): Observable<String> {
    if (!this.patientCareTypeObservable) {
      this.patientCareTypeObservable = new Observable<String>((observer) => {
        this.patientCareTypeObservers.push(observer);
      });
    }
    return this.patientCareTypeObservable;
  }

  private notifyPatientCareTypeChange(): void {
    for (let observer of this.patientCareTypeObservers) {
      observer.next(this.patientCareType);
    }
  }

  //  ##############################
  //  Publish Patient Chart Status changes
  //  ##############################

  public get hasPatientChartStatus() {
    return this.patientChartStatus && this.patientChartStatus !== "";
  }

  public getPatientChartStatus(): String {
    return this.patientChartStatus;
  }

  public updatePatientChartStatus(chartStatus: String) {
    this.patientChartStatus = chartStatus;
    this.notifyPatientChartStatusChange();
  }

  getPatientChartStatusObserver(): Observable<String> {
    if (!this.patientChartStatusObservable) {
      this.patientChartStatusObservable = new Observable<String>((observer) => {
        this.patientChartStatusObservers.push(observer);
      });
    }
    return this.patientChartStatusObservable;
  }

  private notifyPatientChartStatusChange(): void {
    for (let observer of this.patientChartStatusObservers) {
      observer.next(this.patientChartStatus);
    }
  }

  public getTokenRefreshedSubject(): BehaviorSubject<string | null> {
    return this.tokenRefreshedSubject;
  }

  public beginManualTokenRefresh(): void {
    this.isManualTokenRefreshInProgress = true;
    this.tokenRefreshedSubject.next(null);
  }

  public endManualTokenRefresh(newToken: string | null): void {
    this.isManualTokenRefreshInProgress = false;
    if (newToken) {
      this.tokenRefreshedSubject.next(newToken);
    }
  }

  getToggleStartDate(
    toggleName: string,
    returnRawOrConverted: string = "converted"
  ): Observable<Date | String | null> {
    return this.featureToggleDataService.getFeatureToggleDetails().pipe(
      map((toggles) => {
        const found = toggles.find(
          (t) => t.featureName.toLocaleLowerCase() === toggleName.toLowerCase()
        );
        if (found?.startDatetime) {
          switch (returnRawOrConverted) {
            case "raw":
              return found.startDatetime;
            case "converted":
              return new Date(found.startDatetime);
            default:
              return null;
          }
        }
      })
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/state-management/state-management.service.ts